import {
  formatRequestWindow,
  resolveRequestWindows,
} from "utils/requestWindows";

export const hasRtoStatus = (order) =>
  Boolean(
    order?.is_rto ||
      String(order?.fulfillment_status || "")
        .toLowerCase()
        .includes("rto"),
  );

export const hasCancelledStatus = (order) =>
  Boolean(
    order?.is_cancelled ||
      order?.cancelled_at ||
      String(order?.fulfillment_status || "")
        .toLowerCase()
        .includes("cancel"),
  );

export const isOrderActionable = (order) =>
  Boolean(
    order?.eligible &&
      order?.delivered_at &&
      !hasCancelledStatus(order) &&
      !hasRtoStatus(order),
  );

export const isOrderTrackable = (order) =>
  !isOrderActionable(order) && Boolean(order?.rr_exists);

export const getOrderTotal = (order) => {
  const directTotal =
    order?.total_order_price ??
    order?.total_price ??
    order?.current_total_price ??
    order?.order_total ??
    order?.order_amount;

  if (
    directTotal !== undefined &&
    directTotal !== null &&
    directTotal !== ""
  ) {
    return directTotal;
  }

  return (order?.order_items || []).reduce((total, item) => {
    const itemPrice =
      Number(item.discounted_price ?? item.product_price ?? item.price ?? 0) ||
      0;
    const quantity = Number(item.quantity || 1) || 1;
    return total + itemPrice * quantity;
  }, 0);
};

export const formatMoney = (value) => {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return value ? `₹ ${value}` : "₹ 0";
  }

  return `₹ ${numericValue.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;
};

export const getOrderItemCount = (order) =>
  (order?.order_items || []).reduce(
    (count, item) => count + (Number(item.quantity || 1) || 1),
    0,
  );

export const getEligibilityLines = (order, rmsConfig) => {
  if (hasCancelledStatus(order)) {
    return ["Not eligible • Order cancelled"];
  }

  if (hasRtoStatus(order)) {
    return ["Not eligible • Order RTOed"];
  }

  if (!order?.delivered_at) {
    return ["Not eligible • Not yet delivered"];
  }

  if (!isOrderActionable(order)) {
    return ["Not eligible"];
  }

  const windows = resolveRequestWindows({
    subject: order,
    rmsConfig,
    forceExchange: order?.force_exchange,
  });
  const exchangeLabel = rmsConfig?.only_replacement
    ? "Replacement"
    : "Exchange";

  if (windows.sameDate) {
    return [
      `Return/${exchangeLabel} available till ${formatRequestWindow(
        windows.returnDate,
      )}`,
    ];
  }

  const lines = [];
  if (windows.returnDate) {
    lines.push(
      `Return available till ${formatRequestWindow(windows.returnDate)}`,
    );
  }
  if (windows.exchangeDate) {
    lines.push(
      `${exchangeLabel} available till ${formatRequestWindow(
        windows.exchangeDate,
      )}`,
    );
  }

  return lines.length > 0 ? lines : ["Eligible"];
};

export const sortOrdersByEligibility = (orders = []) =>
  orders
    .map((order, index) => ({ order, index }))
    .sort((left, right) => {
      const leftActionable = isOrderActionable(left.order);
      const rightActionable = isOrderActionable(right.order);
      const leftTrackable = isOrderTrackable(left.order);
      const rightTrackable = isOrderTrackable(right.order);

      if (leftActionable !== rightActionable) {
        return leftActionable ? -1 : 1;
      }

      if (leftTrackable !== rightTrackable) {
        return leftTrackable ? -1 : 1;
      }

      return left.index - right.index;
    })
    .map(({ order }) => order);

export const filterOrdersByQuery = (orders = [], query = "") => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return orders;
  }

  return orders.filter((order) =>
    [order.order_name, order.order_number, order.order_id]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery)),
  );
};
